import { useCallback, useEffect, useRef, useState } from 'react'

import type { GuestMedia } from '~publish/helpers/guestMedia'
import { getGuestMediaAccess } from '~publish/pages/Create/PublicComposerPage/guestMediaClient'

const ACCESS_URL_STALE_MS = 55 * 60 * 1000
const MAX_TIMEOUT_MS = 2_147_483_647

export type GuestMediaAccessState =
  | Readonly<{ status: 'loading'; assetExpiresAt: number }>
  | Readonly<{
      status: 'ready'
      accessUrl: string
      accessUrlExpiresAt: number
      assetExpiresAt: number
    }>
  | Readonly<{
      status: 'error'
      message: string
      assetExpiresAt: number
    }>
  | Readonly<{ status: 'expired'; assetExpiresAt: number }>

export type GuestMediaAccessController = Readonly<{
  states: ReadonlyMap<string, GuestMediaAccessState>
  requestAccess: (media: GuestMedia) => void
  retryAccess: (media: GuestMedia) => void
  reportLoadError: (media: GuestMedia, accessUrl: string) => void
}>

function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'The image could not be loaded'
}

function isExpiredAccessError(error: unknown): boolean {
  return (
    error instanceof Error && error.message.toLowerCase().includes('expired')
  )
}

export function useGuestMediaAccess(): GuestMediaAccessController {
  const [states, setStates] = useState<
    ReadonlyMap<string, GuestMediaAccessState>
  >(() => new Map())
  const statesRef = useRef(states)
  const requestsRef = useRef(new Map<string, Promise<void>>())
  const isMountedRef = useRef(true)

  const updateState = useCallback(
    (
      assetUrl: string,
      resolveState: (
        current: GuestMediaAccessState | undefined,
      ) => GuestMediaAccessState,
    ): void => {
      setStates((currentStates) => {
        const nextStates = new Map(currentStates)
        nextStates.set(assetUrl, resolveState(currentStates.get(assetUrl)))
        statesRef.current = nextStates
        return nextStates
      })
    },
    [],
  )

  const requestAccess = useCallback(
    (media: GuestMedia, force = false): void => {
      const currentTime = Date.now()
      if (currentTime >= media.expiresAt) {
        updateState(media.assetUrl, () => ({
          status: 'expired',
          assetExpiresAt: media.expiresAt,
        }))
        return
      }

      const currentState = statesRef.current.get(media.assetUrl)
      if (
        !force &&
        ((currentState?.status === 'ready' &&
          currentState.accessUrlExpiresAt > currentTime) ||
          currentState?.status === 'loading')
      ) {
        return
      }

      if (requestsRef.current.has(media.assetUrl)) return

      updateState(media.assetUrl, () => ({
        status: 'loading',
        assetExpiresAt: media.expiresAt,
      }))

      const request = getGuestMediaAccess(media)
        .then((access) => {
          if (!isMountedRef.current) return undefined

          const assetExpiresAt = Math.min(
            media.expiresAt,
            access.assetExpiresAt,
          )
          const responseTime = Date.now()
          if (responseTime >= assetExpiresAt) {
            updateState(media.assetUrl, () => ({
              status: 'expired',
              assetExpiresAt,
            }))
            return undefined
          }

          updateState(media.assetUrl, () => ({
            status: 'ready',
            accessUrl: access.accessUrl,
            accessUrlExpiresAt: Math.min(
              assetExpiresAt,
              responseTime + ACCESS_URL_STALE_MS,
            ),
            assetExpiresAt,
          }))
          return undefined
        })
        .catch((error: unknown) => {
          if (!isMountedRef.current) return undefined

          const responseTime = Date.now()
          updateState(media.assetUrl, () =>
            responseTime >= media.expiresAt || isExpiredAccessError(error)
              ? {
                  status: 'expired',
                  assetExpiresAt: media.expiresAt,
                }
              : {
                  status: 'error',
                  message: getErrorMessage(error),
                  assetExpiresAt: media.expiresAt,
                },
          )
          return undefined
        })
        .finally(() => {
          if (requestsRef.current.get(media.assetUrl) === request) {
            requestsRef.current.delete(media.assetUrl)
          }
        })

      requestsRef.current.set(media.assetUrl, request)
    },
    [updateState],
  )

  const retryAccess = useCallback(
    (media: GuestMedia): void => requestAccess(media, true),
    [requestAccess],
  )

  const reportLoadError = useCallback(
    (media: GuestMedia, accessUrl: string): void => {
      const currentState = statesRef.current.get(media.assetUrl)
      if (
        currentState?.status === 'ready' &&
        currentState.accessUrl === accessUrl &&
        Date.now() >= currentState.accessUrlExpiresAt
      ) {
        requestAccess(media, true)
        return
      }

      updateState(media.assetUrl, (latestState) => {
        if (
          latestState?.status !== 'ready' ||
          latestState.accessUrl !== accessUrl
        ) {
          return (
            latestState ?? {
              status: 'error',
              message: 'The image could not be loaded',
              assetExpiresAt: media.expiresAt,
            }
          )
        }

        const assetExpiresAt = Math.min(
          media.expiresAt,
          latestState.assetExpiresAt,
        )
        if (Date.now() >= assetExpiresAt) {
          return {
            status: 'expired',
            assetExpiresAt,
          }
        }

        return {
          status: 'error',
          message: 'The image could not be loaded',
          assetExpiresAt,
        }
      })
    },
    [requestAccess, updateState],
  )

  useEffect(() => {
    statesRef.current = states

    let nextTransitionAt = Number.POSITIVE_INFINITY
    for (const state of states.values()) {
      if (state.status === 'expired') continue

      nextTransitionAt = Math.min(nextTransitionAt, state.assetExpiresAt)
    }

    if (!Number.isFinite(nextTransitionAt)) return

    let timeout: number | undefined
    const expireMedia = (): void => {
      const currentTime = Date.now()
      if (currentTime < nextTransitionAt) {
        timeout = window.setTimeout(
          expireMedia,
          Math.min(MAX_TIMEOUT_MS, nextTransitionAt - currentTime),
        )
        return
      }

      setStates((currentStates) => {
        let nextStates: Map<string, GuestMediaAccessState> | undefined

        for (const [assetUrl, state] of currentStates) {
          if (state.status === 'expired') continue

          if (currentTime >= state.assetExpiresAt) {
            nextStates ??= new Map(currentStates)
            nextStates.set(assetUrl, {
              status: 'expired',
              assetExpiresAt: state.assetExpiresAt,
            })
          }
        }

        statesRef.current = nextStates ?? currentStates
        return statesRef.current
      })
    }

    expireMedia()

    return (): void => window.clearTimeout(timeout)
  }, [states])

  useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
    }
  }, [])

  return { states, requestAccess, retryAccess, reportLoadError }
}
